import { memo, useCallback, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useSearchLayers, useSearchWidget } from "../../../../../hooks";
import { view } from "../../../../../utils/API";
import { SearchIcon } from "../../../../Icons";
import CoordinatesPanel from "../../../../SearchWidget/CoordinatesPanel";
import { focusSearchInput } from "../../../../SearchWidget/helpers";
import Suggestions from "../../../../SearchWidget/Suggestions";

import { isWidgetDisplayed } from "../../../../../esri/widgets/expandUtils";
import { triggerGAEvent } from "../../../../../utils/helper";
import { IconButton, Tooltip } from "../../../../UI";
import {
  SectionItem,
  SectionItemContainer,
} from "../UI/Section/Section-styled";
import {
  StyledSearchContainer,
  StyledSearchInput,
  StyledSuggestionsContainer,
} from "./Search-styled";

export const IoMdClose = (props) => (
  <svg
    stroke="currentColor"
    fill="currentColor"
    strokeWidth="0"
    viewBox="0 0 512 512"
    height="18px"
    width="18px"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path d="M405 136.798L375.202 107 256 226.202 136.798 107 107 136.798 226.202 256 107 375.202 136.798 405 256 285.798 375.202 405 405 375.202 285.798 256z"></path>
  </svg>
);

const Search = ({ open, setOpen, openSearch, expanded, config }) => {
  const inputRef = useRef(null);
  const { t } = useTranslation("common");
  const activeModule = useSelector((state) => state.activeModule);

  useEffect(() => {
    if (!expanded) {
      resetSearch();
    }
  }, [expanded]);

  const {
    search,
    value: searchValue,
    loading,
    suggestions,
    activeCoordinates,
    selectedResultKey,
    showCoordinates,
    showDropdown,
    showNoResults,
    showSuggestions,
    suggestionsLength,
    resetSearch,
    handleInputChange,
    handleFocus,
    handleBlur,
    handleSuggestionClick,
    handleCoordinates,
    handleKeyPress,
    locateCoordinates,
    locateButtonClick,
    suggestionRef,
  } = useSearchWidget(view, config, t, "", true, undefined, undefined, false);

  // Handle search layers
  useSearchLayers(search, view, t, config);

  useEffect(() => {
    if (!search) return;
    resetSearch();
  }, [activeModule]);

  const locateTimer = useRef(null);
  const onSearchInputChange = useCallback(
    async (e) => {
      clearTimeout(locateTimer.current);
      const res = await handleInputChange(e);

      // if (res.type === "coordinates") {
      //   locateTimer.current = setTimeout(() => {
      //     locateCoordinates(res.result);
      //   }, 500);
      // }
    },
    [locateCoordinates, handleInputChange]
  );

  const hasSearch = useMemo(() => {
    return isWidgetDisplayed(config, "Search");
  }, [config]);

  if (!hasSearch) return null;

  return (
    <SectionItemContainer
      style={{
        display: "flex",
        flexDirection: "column",
      }}
      className="onboarding-search"
    >
      <SectionItem
        style={{
          border: expanded ? "1px solid #eee" : "none",
          borderRadius: "100px",
          background: expanded ? "rgba(0,0,0,0.08)" : "none",
          overflow: expanded ? "hidden" : "",
          gap: 0,
        }}
        selected={open}
        onClick={() => {
          if (!open) {
            openSearch();
            triggerGAEvent({
              event: "widget",
              params: {
                widget_name: "Search",
              },
            });
          }

          setTimeout(() => {
            focusSearchInput(inputRef.current);
          }, 100);
        }}
      >
        <Tooltip
          content={t("screen.widget.Search.name")}
          wrapperStyles={{
            position: "absolute",
            width: "100%",
            height: "100%",
          }}
          showOnHover
          blurred
          placement="right"
          offset={16}
          disabled={expanded || open}
        />
        <IconButton style={{ background: !expanded ? undefined : "none" }}>
          <SearchIcon />
        </IconButton>

        {!open ? (
          expanded ? (
            <span>{t("screen.widget.Search.name")}</span>
          ) : null
        ) : (
          <StyledSearchContainer>
            <StyledSearchInput
              value={searchValue}
              onChange={onSearchInputChange}
              ref={inputRef}
              onFocus={handleFocus}
              placeholder={t("screen.widget.Search.placeholder", "Search")}
              disabled={!search}
              onKeyPress={handleKeyPress}
            />
            <IoMdClose
              onClick={() => {
                setOpen(false);
                resetSearch();
              }}
            />
          </StyledSearchContainer>
        )}
      </SectionItem>
      {open && (
        <StyledSuggestionsContainer>
          <Suggestions
            ref={suggestionRef}
            showSuggestions={showSuggestions}
            loading={loading}
            showNoResults={showNoResults}
            suggestions={suggestions}
            showCoordinates={showCoordinates}
            showDropdown={showDropdown}
            selectedResultKey={selectedResultKey}
            suggestionsLength={suggestionsLength}
            handleSuggestionClick={handleSuggestionClick}
            isSearchWidget={true}
            activeCoordinates={activeCoordinates}
            handleCoordinates={handleCoordinates}
            handleKeyPress={handleKeyPress}
            locateCoordinates={locateButtonClick}
            t={t}
            config={config}
            isDropdown={false}
          >
            <CoordinatesPanel
              handleCoordinates={handleCoordinates}
              activeCoordinates={activeCoordinates}
              handleKeyPress={handleKeyPress}
              locateCoordinates={locateButtonClick}
              t={t}
            />
          </Suggestions>
        </StyledSuggestionsContainer>
      )}
    </SectionItemContainer>
  );
};

export default memo(Search);
